import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { isNative } from '../lib/native';
import { trackEvent as trackGaEvent } from '../../components/GoogleAnalytics';
import {
  SHOW_AFTER_MS,
  appCtaHref,
  bannerVariant,
  detectMobileOs,
  hasIosAppStoreId,
  includesAfriqahomeApp,
  isScrollTriggered,
  normalizeBannerPath,
  shouldRenderBanner,
  type AppBannerOs,
  type AppBannerPageType,
  type AppBannerVariant,
} from '../lib/appBanner';
import {
  answerAppBannerSession,
  hasTappedCtaBefore,
  isAnsweredThisSession,
  rememberCtaTap,
} from '../lib/appBannerSession';

/**
 * GROWTH-01 — the web-to-app prompt on the property, syndicated and guide routes.
 *
 * Mobile web only. The rules it obeys live in src/lib/appBanner.ts, which is
 * pure and tested; the session it remembers is read and written only by
 * src/lib/appBannerSession.ts; this file is the part that talks to the browser,
 * and everything below is about doing that safely.
 *
 * ─── ONE SESSION, ONE ASK ────────────────────────────────────────────────────
 * The banner asks once per SESSION — a visit, as src/lib/appBanner.ts defines
 * it: it begins when Afriqahome is opened in a tab and ends when that tab is
 * closed, or once the visitor has been away from it for thirty minutes. The ✕
 * and the CTA both end the asking for that session and nothing longer; the
 * next visit asks again. There is no cooldown measured in days any more.
 *
 * A session can also end while this very page stays open — a phone locked for
 * an hour on a listing. The visibility effect below notices the visitor coming
 * back to a page whose banner they answered in a session that has since ended,
 * and arms the banner again, as a new session on a new pageview would.
 *
 * ─── TWO BANNERS, ONE COMPONENT ──────────────────────────────────────────────
 * "Get the app" for a phone without it; "Open in app" for a phone with it. The
 * web can only sometimes tell which it is talking to: Chrome on Android answers
 * `navigator.getInstalledRelatedApps()` (once the app declares this site and
 * public/manifest.webmanifest names the app); every other browser, and every
 * iOS browser, cannot say, and the install hint — this browser tapped the CTA
 * before — decides instead. The rule is `bannerVariant`; the copy for each is
 * `COPY` below, and it is written so that neither is wrong if the guess is.
 *
 * ─── THE HYDRATION RULE ──────────────────────────────────────────────────────
 * /property/@slug is server-rendered by Vike. This component therefore returns
 * `null` on the server AND on the first client render — `target` starts null
 * and `visible` starts false, and only an effect can change either. React 19
 * compares the server HTML against the first client render and throws the whole
 * subtree away on a mismatch, so a component that "knows" it is on Android
 * during that first render is a component that breaks hydration. It is the same
 * shape src/components/home/useClientOnlyStorage.ts uses, and for the same
 * reason. Nothing here reads `window`, `navigator` or either storage during
 * render — the CTA href and the variant are computed inside an effect and held
 * in state.
 *
 * ─── THE NATIVE GUARD, TWICE ─────────────────────────────────────────────────
 * `isNative()` answers "is this the Capacitor BUILD?", which is the primary
 * gate. It is not the only one, because capacitor.config.ts allows the app's
 * webview to navigate to www.afriqahome.com — where it would be running the WEB
 * bundle, with `isNative()` correctly false, and would be the one place an
 * "install our app" bar could appear inside the app. `detectMobileOs` refuses
 * the native shell's `Afriqahome/iOS`|`Afriqahome/Android` user agent for
 * exactly that case.
 *
 * ─── ZERO LAYOUT SHIFT ───────────────────────────────────────────────────────
 * `position: fixed`, always. It overlays; it never reserves space, never pushes
 * content, and dismissing it restores nothing because it took nothing. It also
 * must not COVER whatever is already anchored to the bottom of the screen, so
 * it measures every marked bottom control (`[data-app-banner-anchor]`: the
 * property page's sticky price bar, BottomNav, and the consent banner) and sits
 * above the tallest one that is actually visible — see `measureAnchor`.
 *
 * ─── ONE LISTING, ONE PAGEVIEW ───────────────────────────────────────────────
 * The property and syndicated routes are also React Router routes, so a visitor
 * can move from one listing to another without a page load. That is a new
 * pageview: the timer restarts, the deep link is rebuilt for the new path, and
 * the impression is allowed to fire again. Getting this wrong is not a cosmetic
 * bug — it reports an impression for a banner that is not on screen. See the
 * state reset immediately below, which is deliberately in the RENDER phase.
 */

const ANCHOR_SELECTOR = '[data-app-banner-anchor]';

/**
 * Where the banner sits when there is genuinely nothing at the bottom of the
 * screen to clear — a small inset off the edge, safe area included.
 *
 * This used to be `--fab-bottom-offset` (index.css's "above the floating nav"
 * constant), which assumed a nav that is not always there: `BottomNav` is
 * `md:hidden`, and /property's sticky bar is `lg:hidden`, so from those
 * breakpoints up the banner floated ~96px above an empty screen edge on a
 * tablet or a phone in landscape. Both bottom controls are marked with
 * ANCHOR_SELECTOR now and measured directly, so this branch means what it says.
 */
const FALLBACK_BOTTOM = 'calc(env(safe-area-inset-bottom, 0px) + 12px)';

/** Breathing room between the banner and whatever it sits on top of. */
const ANCHOR_GAP_PX = 8;

const ENTER_MS = 260;

/**
 * THE COPY, in one place, as data.
 *
 * The "get" lines are the ones the ticket locked. The "open" lines say the
 * same thing to a phone that (most likely) has the app already: keep going in
 * the app instead. Neither claims to know the phone — "open" is a nudge, not a
 * statement — so the wrong variant is a weaker banner, never a false one. Plain
 * words, one action, no urgency: the house voice.
 */
const COPY: Record<AppBannerVariant, { headline: string; body: string; cta: string }> = {
  get: {
    headline: 'Faster in the app',
    body: 'Save homes and message verified agents on the go.',
    cta: 'Get the app',
  },
  open: {
    headline: 'Faster in the app',
    body: 'Keep browsing in the Afriqahome app instead.',
    cta: 'Open in app',
  },
};

/**
 * The height of the tallest bottom control currently on screen, or null when
 * there is nothing down there to clear.
 *
 * ALL matches, not the first: /property/:slug marks its sticky price bar and
 * BottomNav marks itself, and while today they are never both present on one
 * route (BottomNav returns null on /property/*), "the first one in the DOM" is
 * not a rule that survives someone adding a second bar.
 *
 * `offsetHeight`, not `getBoundingClientRect()`: BottomNav slides itself out of
 * view on scroll, and a transformed rect would drag the banner along with it.
 * offsetHeight is the layout box and stays put. It is also 0 when an anchor is
 * `display: none` — which is exactly how both `md:hidden` on the nav and
 * `lg:hidden` on the sticky bar report "not at this width", so the banner drops
 * to FALLBACK_BOTTOM instead of reserving room for something invisible.
 */
function measureAnchor(): number | null {
  let tallest = 0;
  document.querySelectorAll<HTMLElement>(ANCHOR_SELECTOR).forEach((anchor) => {
    if (anchor.offsetHeight > tallest) tallest = anchor.offsetHeight;
  });
  return tallest > 0 ? tallest : null;
}

export interface MobileAppPromptProps {
  pageType: AppBannerPageType;
  /**
   * What the visitor is looking at: `property.id` on /property/:slug, the
   * route's `:id` on /syndicated/:id, the guide's slug on /guides/:slug.
   *
   * Named for the content rather than for a property because guides are not
   * listings. The GA4 PARAMETER it is reported under stays `property_id` — that
   * key is locked, and renaming it would split every existing report in two at
   * the moment of deploy. `page_type` is what tells the two apart, and the
   * values never collide (UUIDs vs slugs).
   *
   * Also the "one impression per pageview" identity, so a client-side move
   * between two listings counts as two pageviews.
   */
  contentId: string;
}

/**
 * Renders nothing at all unless: not the native build, a phone we can place,
 * no answer yet in this session, and (on iOS) a real App Store id — then
 * nothing still, until 10 seconds have passed or the visitor has scrolled 40%
 * of the way down.
 */
const MobileAppPrompt: React.FC<MobileAppPromptProps> = ({ pageType, contentId }) => {
  const [target, setTarget] = useState<{ os: AppBannerOs; href: string; variant: AppBannerVariant } | null>(null);
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);
  const [enterMs, setEnterMs] = useState(ENTER_MS);
  const [anchorPx, setAnchorPx] = useState<number | null>(null);

  /**
   * Counts the sessions this pageview has lived through. Bumped by the
   * visibility effect when the session in which this banner was answered has
   * ended while the page stayed open — which re-runs the eligibility effect,
   * exactly as a new pageview would.
   */
  const [sessionEpoch, setSessionEpoch] = useState(0);

  const pageviewKey = `${pageType}:${contentId}`;
  /** One showing: this pageview, in this session. What the events are keyed by. */
  const showKey = `${pageviewKey}#${sessionEpoch}`;

  /** Which showing has already reported each event. */
  const impressionFiredFor = useRef<string | null>(null);
  const clickReportedFor = useRef<string | null>(null);
  /** Whether THIS pageview's banner has been answered (✕ or CTA) in the current session. */
  const answeredHere = useRef(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  // ---------------------------------------------------------------------------
  // A NEW LISTING IS A NEW PAGEVIEW — AND THIS RESET IS IN THE RENDER PHASE.
  //
  // React's documented "adjusting state when a prop changes" escape hatch, and
  // it has to be here rather than in the effect below. Effects run AFTER the
  // browser has painted. If the reset lived in the effect, then on a
  // client-side move from listing A to listing B React would first paint one
  // frame of B's page carrying A's banner — with A's `intent://` deep link on
  // the button — and the impression effect, which also runs after that paint,
  // would see the stale `visible === true` and report an impression for B while
  // B's banner was not on screen. Worse, that write to `impressionFiredFor`
  // then suppressed B's REAL impression ten seconds later. Both were observed
  // in Chromium before this moved out of the effect.
  //
  // Re-rendering during render is cheap here: React discards the in-progress
  // render and immediately retries with the cleared state, before any paint.
  // ---------------------------------------------------------------------------
  const [renderedKey, setRenderedKey] = useState(pageviewKey);
  if (renderedKey !== pageviewKey) {
    setRenderedKey(pageviewKey);
    setTarget(null);
    setVisible(false);
    setEntered(false);
    setAnchorPx(null);
  }

  // ---------------------------------------------------------------------------
  // ELIGIBILITY, THEN THE TRIGGER
  //
  // One effect, because the trigger must not be armed for a visitor who is not
  // eligible — a timer that fires for somebody who already answered this
  // session is a bug even if the render is guarded, since it would report an
  // impression.
  //
  // Re-runs per pageview AND per session (`sessionEpoch`): both are a fresh
  // showing, with a fresh timer and a fresh impression.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    answeredHere.current = false;

    const nativeApp = isNative();

    const os = detectMobileOs(window.navigator.userAgent, {
      platform: window.navigator.platform,
      maxTouchPoints: window.navigator.maxTouchPoints,
    });

    const eligible = shouldRenderBanner({
      isNativeApp: nativeApp,
      os,
      answeredThisSession: isAnsweredThisSession(),
      iosStoreIdResolved: hasIosAppStoreId(),
    });
    if (!eligible || !os) return;

    const path = normalizeBannerPath(window.location.pathname);

    // WHICH BANNER. Chrome on Android can be asked whether the app is on the
    // phone; asked now, so the answer is normally in hand long before the
    // trigger fires, and read at reveal. Anyone the browser cannot speak for
    // stays `null` and the install hint decides. Never awaited: a slow or
    // absent answer must not delay the banner, and the copy must not change
    // under the visitor's thumb once shown — so it is read exactly once.
    let installed: boolean | null = null;
    let cancelled = false;
    if (os === 'android' && typeof window.navigator.getInstalledRelatedApps === 'function') {
      try {
        window.navigator
          .getInstalledRelatedApps()
          .then((apps) => {
            if (!cancelled) installed = includesAfriqahomeApp(apps);
          })
          .catch(() => {
            // Unknown stays unknown.
          });
      } catch {
        // Some embedders throw synchronously (a cross-origin frame). Same answer.
      }
    }

    let fired = false;
    const reveal = () => {
      if (fired) return;
      fired = true;
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);

      // Measured HERE, in the same batch as `visible`, so the banner's very
      // first paint is already at its final offset. Revealing first and
      // measuring from an effect afterwards moves it from the fallback offset
      // to the measured one a frame later — small, but a real layout shift
      // (0.009 CLS, measured in Chromium), and the ticket asks for zero.
      setAnchorPx(measureAnchor());
      const variant = bannerVariant({ installed, tappedBefore: hasTappedCtaBefore() });
      setTarget({ os, href: appCtaHref(os, path), variant });
      setVisible(true);
    };

    const onScroll = () => {
      if (
        isScrollTriggered({
          scrollY: window.scrollY,
          viewportHeight: window.innerHeight,
          documentHeight: document.documentElement.scrollHeight,
        })
      ) {
        reveal();
      }
    };

    // Never on initial paint: the timer is the earliest either branch can fire,
    // and the scroll branch is only ever evaluated from a real scroll event.
    const timer = window.setTimeout(reveal, SHOW_AFTER_MS);
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      fired = true;
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [pageType, contentId, sessionEpoch]);

  // ---------------------------------------------------------------------------
  // A SESSION THAT ENDS WHILE THE PAGE IS OPEN
  //
  // The visitor answered the banner on this page, put the phone down, and came
  // back an hour later to the same tab. Thirty idle minutes ended that session
  // (the heartbeat in +Layout stamped the moment they left), so by the rule
  // this is a new visit and the banner may ask again — and it does, by bumping
  // `sessionEpoch`, which re-runs the effect above from the top.
  //
  // Three guards, so this fires only for exactly that case: the page is
  // becoming visible; THIS pageview's banner was answered (a banner still
  // waiting on its timer is left alone); and the session on record no longer
  // carries that answer.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!answeredHere.current) return;
      if (isAnsweredThisSession()) return;
      setSessionEpoch((epoch) => epoch + 1);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // ---------------------------------------------------------------------------
  // IMPRESSION — when it is ACTUALLY ON SCREEN, once per showing.
  //
  // Not when `visible` flips. That is the state machine's opinion, and CSS gets
  // the last word: while this component carried an `md:hidden`, an Android
  // tablet reported an impression for a `display: none` banner (observed in
  // Chromium at 900px). The class is gone, but the class of bug is not — any
  // future breakpoint, `print:hidden` in a print preview, or a parent that
  // hides this subtree would resurrect it. So the browser is asked directly:
  // a `display: none` element has an empty rect and never intersects.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!visible || !target) return;
    if (impressionFiredFor.current === showKey) return;

    const fire = () => {
      if (impressionFiredFor.current === showKey) return;
      impressionFiredFor.current = showKey;
      trackGaEvent('app_banner_impression', {
        page_type: pageType,
        property_id: contentId,
        os: target.os,
        variant: target.variant,
      });
    };

    const node = bannerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      fire();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        fire();
        observer.disconnect();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible, target, showKey, pageType, contentId]);

  // ---------------------------------------------------------------------------
  // ENTRANCE — opacity only.
  //
  // It used to slide up from `translateY(120%)`, which starts the banner DOWN
  // ON TOP OF the sticky CTA and crosses it for 260ms. Both are z-40 and this
  // one is later in the DOM, so it wins that paint: "must not overlap or
  // obscure the sticky CTA" was true at rest and false during the animation.
  // A fade neither moves nor overlaps, and — like a transform — it costs no
  // layout, so CLS stays at zero.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!visible) {
      setEntered(false);
      return;
    }
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setEnterMs(reduced ? 0 : ENTER_MS);
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, [visible]);

  // ---------------------------------------------------------------------------
  // KEEPING THE BOTTOM CORRECT
  //
  // The first value was taken at reveal time, above. This only has to notice
  // the bar CHANGING afterwards — a rotation, a price line that rewraps, the
  // USD approximation arriving late.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!visible) return;

    const anchors = document.querySelectorAll<HTMLElement>(ANCHOR_SELECTOR);
    if (anchors.length === 0) return;

    const measure = () => setAnchorPx(measureAnchor());

    // A breakpoint crossing that flips an anchor to `display: none` resizes it
    // to 0x0, so the observer catches that as well as a price line rewrapping.
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      anchors.forEach((anchor) => observer?.observe(anchor));
    }
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [visible]);

  const handleDismiss = useCallback(() => {
    if (!target) return;
    answerAppBannerSession('dismiss');
    answeredHere.current = true;
    trackGaEvent('app_banner_dismiss', {
      page_type: pageType,
      property_id: contentId,
      os: target.os,
      variant: target.variant,
    });
    setVisible(false);
  }, [target, pageType, contentId]);

  // No preventDefault, no router: the href is either an `intent://` URL the OS
  // has to see or an App Store link. The navigation is the point.
  //
  // The banner closes on the way out. An Android intent that reaches a chooser
  // the visitor then cancels leaves them back on this page, and a banner still
  // sitting there invites a second tap — a second app_banner_click for one
  // decision, inside the session that tap just answered. The ref makes the
  // event once-per-showing even if the click somehow repeats.
  //
  // The tap is also the install hint: from the next session on, this browser
  // is shown "Open in app" unless Chrome can say otherwise.
  const handleCtaClick = useCallback(() => {
    if (!target) return;
    answerAppBannerSession('click');
    rememberCtaTap();
    answeredHere.current = true;
    if (clickReportedFor.current !== showKey) {
      clickReportedFor.current = showKey;
      trackGaEvent('app_banner_click', {
        page_type: pageType,
        property_id: contentId,
        os: target.os,
        variant: target.variant,
      });
    }
    setVisible(false);
  }, [target, showKey, pageType, contentId]);

  if (!visible || !target) return null;

  const copy = COPY[target.variant];
  const bottom = anchorPx === null ? FALLBACK_BOTTOM : `${anchorPx + ANCHOR_GAP_PX}px`;

  return (
    <div
      ref={bannerRef}
      role="complementary"
      aria-label="Afriqahome app"
      data-app-banner-variant={target.variant}
      /*
        NO VIEWPORT GATE. Locked decision 2 defines the audience by user agent —
        "mobile web only (Android/iOS)" — and an `md:hidden` here would have
        quietly narrowed that to phones, cutting out every Android tablet the UA
        check just admitted. It would also disagree with the state machine,
        which is how an impression gets reported for a `display: none` banner.
        `max-w-md mx-auto` is what keeps it from stretching on a wide screen.

        `z-40` deliberately matches the sticky CTA it sits above: below the
        modals (z-50), and in the same relationship to the Leaflet controls
        (z-[400]) that the sticky bar already has. One rule for both bars.
      */
      className="fixed inset-x-0 z-40 px-3 print:hidden"
      style={{
        bottom,
        opacity: entered ? 1 : 0,
        transition: `opacity ${enterMs}ms ease-out`,
        willChange: 'opacity',
      }}
    >
      <div className="relative mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-gray-200 bg-white/95 py-2.5 pl-3 pr-11 shadow-[0_-4px_30px_rgba(0,0,0,0.12)] backdrop-blur-md">
        {/*
          NO `truncate` ON EITHER LINE. The body copy is locked and 49
          characters long; in this row it has ~130px, which rendered it as
          "Save homes and mess…" on a 390px phone — the copy the ticket froze,
          shown as an ellipsis. It wraps instead, and the bar is as tall as its
          own copy needs.
        */}
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-bold leading-tight text-brand-text">
            {copy.headline}
          </p>
          <p className="mt-0.5 text-[11.5px] leading-snug text-gray-500">
            {copy.body}
          </p>
        </div>

        <a
          href={target.href}
          onClick={handleCtaClick}
          className="flex min-h-[44px] shrink-0 items-center rounded-xl bg-brand-primary px-3.5 text-[13px] font-semibold text-white transition-colors active:bg-brand-primary/90"
        >
          {copy.cta}
        </a>

        {/*
          The dismiss control. Locked decision 5 says an X button; locked copy 7
          says the dismiss word is "Not now" — so "Not now" is this button's
          accessible name rather than a second visible control beside the one
          CTA. 44x44 hit area over a small glyph, the same rule the save heart
          and the signed-out prompt's ✕ follow: a dismiss you cannot hit is a
          dark pattern whether or not it was meant as one.
        */}
        {/*
          Positioned into the corner rather than sitting in the row: at 390px
          the row has ~180px for two lines of locked copy, and a 44px column for
          the ✕ was taking a fifth of it. The card's `pr-11` reserves the space
          it occupies, so it overlaps nothing.
        */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Not now"
          className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center rounded-full text-gray-500 transition-colors active:text-gray-700"
        >
          <X size={17} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

export default MobileAppPrompt;
